import React, { useRef, useEffect, useState } from 'react';

const LifeMapViewer = React.memo(({ data, theme, selectedSample }) => {
  const containerRef = useRef(null);
  const lifemapRef = useRef(null);
  const [libLoaded, setLibLoaded] = useState(false);
  const [highlightCoords, setHighlightCoords] = useState(null);
  const highlightElRef = useRef(null);

  // Load library check
  useEffect(() => {
    const checkLib = () => {
      if (window.Lifemap?.Lifemap) setLibLoaded(true);
      else setTimeout(checkLib, 200);
    };
    checkLib();
  },[]);

  // 1. Initialize instance and Monkey Patch (Runs strictly on mount/theme change)
  useEffect(() => {
    if (!libLoaded || !containerRef.current) return;

    if (!lifemapRef.current) {
      const { offsetWidth: w, offsetHeight: h } = containerRef.current;
      if (w === 0 || h === 0) return;
      
      lifemapRef.current = new window.Lifemap.Lifemap(containerRef.current, {
        theme: theme, width: w, height: h, zoom: 1,
      });

      // --- AUTO-ZOOM PATCH (Original untouched) ---
      const origUpdateLayers = lifemapRef.current.update_layers.bind(lifemapRef.current);
      lifemapRef.current.update_layers = async function(layersConfig, colorRanges) {
        await origUpdateLayers(layersConfig, colorRanges);
        try {
          const view = this.map.getView();
          let ext =[Infinity, Infinity, -Infinity, -Infinity];
          let hasPoints = false;
          for (let layer of this.ol_layers) {
            const source = layer.getSource();
            if (source) {
              const layerExt = source.getExtent();
              if (layerExt && layerExt[0] !== Infinity && layerExt[0] !== undefined) {
                hasPoints = true;
                ext[0] = Math.min(ext[0], layerExt[0]);
                ext[1] = Math.min(ext[1], layerExt[1]);
                ext[2] = Math.max(ext[2], layerExt[2]);
                ext[3] = Math.max(ext[3], layerExt[3]);
              }
            }
          }
          if (hasPoints && ext[0] !== Infinity) {
            view.fit(ext, { 
              padding:[100, 100, 100, 100], 
              duration: 800, 
              maxZoom: 20 
            });
          }
        } catch (err) {
          console.warn("Lifemap auto-zoom failed", err);
        }
      };
      // -----------------------
    }

    return () => {
      if (containerRef.current) containerRef.current.style.pointerEvents = 'none';
      if (lifemapRef.current) {
        lifemapRef.current.destroy();
        lifemapRef.current = null;
      }
    };
  }, [libLoaded, theme]);

  // 2. Data Rendering (Runs ONLY when base `data` changes)
  useEffect(() => {
    if (!lifemapRef.current || !libLoaded) return;
    
    const processed = (data ||[]).map(row => {
      let taxid = Number(row?.tax_id || row?.taxon_id || row?.TAX_ID || row?.HOST_TAXID);
      taxid = isNaN(taxid) ? null : Math.floor(taxid);
      return isNaN(taxid) || taxid === 0 || !Number.isInteger(taxid) ? null : {
        pylifemap_taxid: taxid, pylifemap_x: 0, pylifemap_y: 0, pylifemap_zoom: 0
      };
    }).filter(Boolean);

    const container = containerRef.current;
    if (container) container.style.pointerEvents = 'none';
    
    // Pass exclusively the base points
    lifemapRef.current.update({
      data: { 
        layer_points: { serialized: false, value: processed }
      },
      layers:[
        {
          layer: "points",
          id: "layer_points",
          options: { fill: '#fb0000', radius: 3, hover: true, popup: true, lazy: false }
        }
      ]
    });

    const timer = setTimeout(() => {
      if (container) container.style.pointerEvents = 'auto';
    }, 300);

    return () => clearTimeout(timer);
  }, [data, libLoaded]);

  // 3. Selection Focus (Runs when user clicks a row, no camera movement)
  useEffect(() => {
    if (!lifemapRef.current || !libLoaded || !data) return;

    if (!selectedSample) {
      setHighlightCoords(null);
      return;
    }

    let taxid = Number(selectedSample.tax_id || selectedSample.taxon_id || selectedSample.TAX_ID || selectedSample.HOST_TAXID);
    taxid = isNaN(taxid) ? null : Math.floor(taxid);
    
    if (!taxid) {
      setHighlightCoords(null);
      return;
    }

    // Solr fetching might take a fraction of a second, poll until coordinate is resolved by lifemap.js
    let intervalId = setInterval(() => {
      const mapInstance = lifemapRef.current;
      if (!mapInstance || !mapInstance.data) return;

      const lmData = mapInstance.data.layer_points;
      if (!lmData) return;

      const point = lmData.find(r => r.pylifemap_taxid === taxid);
      
      if (point && point.pylifemap_x !== 0 && point.pylifemap_x !== undefined) {
        clearInterval(intervalId);
        
        // Just record the coordinates for the HTML overlay, no camera animation
        setHighlightCoords([point.pylifemap_x, point.pylifemap_y]);
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [selectedSample, data, libLoaded]);

  // 4. Keep HTML Overlay Synced with Map Panning/Zooming natively
  useEffect(() => {
    const map = lifemapRef.current?.map;
    const highlightEl = highlightElRef.current;
    
    if (!map || !highlightEl) return;

    const renderHighlight = () => {
      if (!highlightCoords) {
        highlightEl.style.display = 'none';
        return;
      }
      
      // Calculate precise screen pixels from Map coordinates (runs at ~60fps during pan)
      const pixel = map.getPixelFromCoordinate(highlightCoords);
      if (pixel) {
        highlightEl.style.display = 'block';
        highlightEl.style.transform = `translate(${pixel[0]}px, ${pixel[1]}px) translate(-50%, -50%)`;
      } else {
        highlightEl.style.display = 'none';
      }
    };

    // Render immediately
    renderHighlight();
    
    // Bind to the map's render loop
    map.on('postrender', renderHighlight);

    return () => {
      if (typeof map.un === 'function') {
        map.un('postrender', renderHighlight);
      } else if (typeof map.removeEventListener === 'function') {
        map.removeEventListener('postrender', renderHighlight);
      }
    };
  }, [highlightCoords]);

  return (
    <div className="w-full h-full bg-white relative overflow-hidden">
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />
      
      {/* Absolute floating marker rendering precisely over the canvas */}
      <div 
        ref={highlightElRef}
        className="pointer-events-none absolute top-0 left-0 z-[1000] border-2 border-white shadow-md"
        style={{
          width: '15px',
          height: '15px',
          borderRadius: '50%',
          backgroundColor: '#ff6f00',
          display: 'none',
          boxShadow: '0 0 10px rgba(0, 255, 255, 0.8)'
        }}
      />
    </div>
  );
}, (prev, next) => {
  // Check if the selection was updated
  const prevSelectedId = prev.selectedSample?.tax_id || prev.selectedSample?.taxon_id || prev.selectedSample?.TAX_ID || prev.selectedSample?.HOST_TAXID;
  const nextSelectedId = next.selectedSample?.tax_id || next.selectedSample?.taxon_id || next.selectedSample?.TAX_ID || next.selectedSample?.HOST_TAXID;
  if (prevSelectedId !== nextSelectedId) return false;

  if (prev.data === next.data) return true;
  if (prev.data?.length !== next.data?.length) return false;
  
  return prev.data.every((row, i) => {
    const nextRow = next.data[i];
    const prevId = row?.tax_id || row?.taxon_id || row?.TAX_ID || row?.HOST_TAXID;
    const nextId = nextRow?.tax_id || nextRow?.taxon_id || nextRow?.TAX_ID || nextRow?.HOST_TAXID;
    return prevId === nextId;
  });
});

export default LifeMapViewer;